/**
 * Pipeline Health — "What is the state of our backlog right now?"
 *
 * Route: /prioritization/reports/pipeline-health  (roadmap §2.1)
 * Print: /prioritization/reports/pipeline-health/print
 * Replaces the Item Readiness report, whose three charts this page absorbs.
 *
 * ── What every number is ───────────────────────────────────────────────────
 * Counted from the items store with the predicates the rest of the product
 * already uses: triage (Triage Queue), definition (Needs Definition page),
 * readiness (lib/itemReadiness), pools, sessions, priority lists. The funnel
 * classifier lives in lib/pipelineHealth.ts and the item list carries the same
 * value as `pipelineStage`, so clicking a stage opens exactly the rows it
 * counted. Nothing here is a second definition of anything.
 *
 * ── The shared report frame (roadmap RP-7) ─────────────────────────────────
 * Range (created window, custom days, or one cycle) and Segment by come from
 * the frame, as on every report; queue, org unit and the stale threshold are
 * this page's own filters. All of it lives in the URL. Every link carries the
 * same narrowing: with no narrowing a stage opens `?stage=<bucket>` as before;
 * with any, it opens the list with a statement that names the stage AND the
 * range, queue and org unit — so the count you clicked is the count you get.
 * The frame also gives export (CSV, print view, charts as PNG), snapshots and
 * scheduled digests.
 *
 * ── Honest limits ──────────────────────────────────────────────────────────
 * "Age" is days since the item last changed (updated_at). The product stores
 * no "entered current state" stamp for active items; the list's Stale filter
 * reads the same column, so stale means one thing everywhere.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Grid3x3, ListChecks, TableProperties } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import {
  Callout,
  DataTable,
  EmptyState,
  Field,
  Panel,
  Select,
  Skeleton,
  Tabs,
  chartTheme,
  resolveToken,
  type Column,
} from '../../../components/ds';
import { useTheme } from '../../../contexts/ThemeContext';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useItemPoolsStore } from '../../../stores/prioritization/itemPoolsStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useOrgUnits } from '../../../stores/prioritization/enterpriseIdentityStore';
import { usePipelineInputs } from '../../../hooks/prioritization/usePipelineInputs';
import { useItemSpaceScope } from '../../../hooks/prioritization/useItemSpaceScope';
import { describeSpaceScope, scopeItemsToSpace } from '../../../lib/itemSpaceScope';
import { useUserNameMap } from '../../../hooks/useUserNameMap';
import { computeItemReadiness } from '../../../lib/itemReadiness';
import { buildItemMembership } from '../../../lib/itemMembership';
import { resolveCategoryHex, CATEGORY_HEX_FALLBACK } from '../../../lib/itemCategoryColors';
import { itemExitStatus } from '../../../lib/intakeQueueVisibility';
import {
  DEFAULT_STALE_THRESHOLD,
  PIPELINE_BUCKET_LABEL,
  PIPELINE_EXITS,
  PIPELINE_STAGES,
  STALE_THRESHOLDS,
  UNSET_CATEGORY,
  ageDistribution,
  breakdown,
  categoryMix,
  categoryOf,
  classifyPipelineBucket,
  daysSince,
  readinessGapCells,
  type PipelineBucket,
  type ReadinessGapCell,
} from '../../../lib/pipelineHealth';
import type { Item } from '../../../types/prioritization/lifecycle';
import type { OrgUnit } from '../../../types/prioritization/enterpriseIdentity';
import type { ItemRow } from '../../../components/prioritization/item-list/hooks/useItemListData';
import type { ReportSnapshotSummary } from '../../../lib/reportSnapshotsService';
import { ReportFrame } from '../../../components/prioritization/reports/shared/ReportFrame';
import { clause, drillToItems, inClause } from '../../../components/prioritization/reports/shared/drillToItems';
import { rowInRange } from '../../../components/prioritization/reports/shared/reportRange';
import { segmentDefinition, segmentLabel, segmentValue, type SegmentNames } from '../../../components/prioritization/reports/shared/reportSegment';
import { useReportControls, type ReportControls } from '../../../components/prioritization/reports/shared/useReportControls';
import { ReportPrintShell } from './ReportPrintShell';

// ============================================================================
// Constants
// ============================================================================

const REPORT_KEY = 'pipeline-health';
const REPORT_PATH = '/prioritization/reports/pipeline-health';
const REPORT_NAME = 'Pipeline Health';
const QUESTION = 'What is the state of our backlog right now?';

const HEATMAP_LIMIT = 360;
const ITEMS_HREF = '/prioritization/items';
const DAY_MS = 24 * 60 * 60 * 1000;

const EXIT_STATUS_ORDER = ['active', 'deferred', 'archived'] as const;
type ExitStatus = (typeof EXIT_STATUS_ORDER)[number];
const EXIT_STATUS_LABEL: Record<ExitStatus, string> = { active: 'Active', deferred: 'Deferred', archived: 'Archived' };

const MONO_LABEL = 'font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--text-ter)]';

// ============================================================================
// This page's own filters (range and segment come from the frame)
// ============================================================================

interface Filters {
  queue: string;
  org: string;
  stale: number;
}

function readFilters(params: URLSearchParams): Filters {
  const stale = Number(params.get('stale'));
  return {
    queue: params.get('queue') ?? '',
    org: params.get('org') ?? '',
    stale: STALE_THRESHOLDS.includes(stale) ? stale : DEFAULT_STALE_THRESHOLD,
  };
}

function usePipelineFilters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const setFilter = useCallback(
    (key: keyof Filters, value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const isDefault = value === '' || (key === 'stale' && Number(value) === DEFAULT_STALE_THRESHOLD);
          if (isDefault) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Links saved before the frame used `by=queue|org` and created windows the
  // frame does not offer by name; carry them over once.
  useEffect(() => {
    const by = searchParams.get('by');
    const range = searchParams.get('range');
    if (!by && range !== '365d' && range !== '180d') return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (by) {
          if (!next.get('seg') && (by === 'queue' || by === 'org')) next.set('seg', by === 'org' ? 'orgUnit' : 'queue');
          next.delete('by');
        }
        if (range === '365d') next.set('range', '1y');
        if (range === '180d') {
          next.set('range', 'custom');
          next.set('from', new Date(Date.now() - 180 * DAY_MS).toISOString().slice(0, 10));
          next.delete('to');
        }
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  return { filters, setFilter };
}

// ============================================================================
// Helpers
// ============================================================================

/** An item is "in" an org unit when that unit is on its path (so a department
    filter includes its teams). Falls back to an exact match when no path is known. */
function orgMatches(item: Item, orgId: string, unitsById: Map<string, OrgUnit>): boolean {
  if (!orgId) return true;
  if (!item.orgUnitId) return false;
  if (item.orgUnitId === orgId) return true;
  const path = item.orgUnitPath ?? unitsById.get(item.orgUnitId)?.path;
  return Array.isArray(path) && path.includes(orgId);
}

/** The org unit and every unit under it — the same set orgMatches accepts. */
function orgAndDescendants(orgId: string, units: ReadonlyArray<OrgUnit>): string[] {
  if (!orgId) return [];
  return units.filter((u) => u.id === orgId || (Array.isArray(u.path) && u.path.includes(orgId))).map((u) => u.id);
}

function unitName(id: string, unitsById: Map<string, OrgUnit>, empty: string): string {
  if (!id) return empty;
  return unitsById.get(id)?.displayName ?? unitsById.get(id)?.name ?? 'Unknown';
}

function categoryLabel(key: string): string {
  if (key === UNSET_CATEGORY) return 'Uncategorized';
  return key.charAt(0).toUpperCase() + key.slice(1);
}

function percent(part: number, whole: number): string {
  if (whole === 0) return '0%';
  return `${Math.round((part / whole) * 100)}%`;
}

/** recharts hands a click either the datum or a wrapper carrying it as `payload`. */
function clickedRow<T extends object>(d: unknown): T | null {
  if (!d || typeof d !== 'object') return null;
  const wrapped = d as { payload?: T };
  return wrapped.payload && typeof wrapped.payload === 'object' ? wrapped.payload : (d as T);
}

interface StatusRow {
  group: string;
  label: string;
  active: number;
  deferred: number;
  archived: number;
  total: number;
}

interface CategoryRow {
  group: string;
  label: string;
  total: number;
  [category: string]: string | number;
}

// ============================================================================
// The model: every number and every link, computed once per render
// ============================================================================

function usePipelineHealthModel(controls: ReportControls, filters: Filters) {
  const { isDark } = useTheme();
  const storeItems = useItemsStore((s) => s.items);
  // The items the item list shows in the current space, by the one rule the
  // desktop and phone lists use (lib/itemSpaceScope.ts) — so every count here
  // is the row count of the list its link opens.
  const spaceScope = useItemSpaceScope();
  const spaceWords = useMemo(() => describeSpaceScope(spaceScope), [spaceScope]);
  const items = useMemo(() => scopeItemsToSpace(storeItems, spaceScope), [storeItems, spaceScope]);
  const itemsLoading = useItemsStore((s) => s.syncState.isLoading);
  const loadError = useItemsStore((s) => s.syncState.syncError);
  const attributes = useAttributesStore((s) => s.attributes);
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const sessions = useSessionStore((s) => s.sessions);
  const orgUnits = useOrgUnits();
  const pipelineInputs = usePipelineInputs();
  const { resolved, segment } = controls;
  const segDef = segmentDefinition(segment);

  // Resolve the chart palette once per theme; recharts needs concrete colours.
  const palette = useMemo(() => {
    void isDark;
    return {
      theme: chartTheme(),
      slate: resolveToken('--slate'),
      gold: resolveToken('--mustard'),
      clay: resolveToken('--coral'),
      dim: resolveToken('--text-dim'),
      textSec: resolveToken('--text-sec'),
    };
  }, [isDark]);

  const exitStatusColor: Record<ExitStatus, string> = useMemo(
    () => ({ active: palette.slate, deferred: palette.gold, archived: palette.dim }),
    [palette],
  );

  const unitsById = useMemo(() => new Map(orgUnits.map((u) => [u.id, u])), [orgUnits]);
  const queues = useMemo(
    () => orgUnits.filter((u) => u.queueSettings?.enabled).sort((a, b) => (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name)),
    [orgUnits],
  );
  const orgOptions = useMemo(
    () =>
      [...orgUnits]
        .sort((a, b) => (a.depth ?? 0) - (b.depth ?? 0) || (a.displayName ?? a.name).localeCompare(b.displayName ?? b.name))
        .map((u) => ({ value: u.id, label: `${'  '.repeat(u.depth ?? 0)}${u.displayName ?? u.name}` })),
    [orgUnits],
  );

  const requiredAttributes = useMemo(
    () => attributes.filter((a) => a.required).map((a) => ({ key: a.key, label: a.label, required: a.required })),
    [attributes],
  );

  const now = useMemo(() => Date.now(), [items]); // eslint-disable-line react-hooks/exhaustive-deps -- re-stamp when the data changes, not every render

  // Cycle membership comes from the same builder the item list uses, so a
  // "Cycle: Q4" range here counts the items the list's Cycle filter shows.
  const membership = useMemo(() => buildItemMembership(itemPools, sessions), [itemPools, sessions]);

  // ── The narrowed population every view reads ─────────────────────────────
  const scoped = useMemo(
    () =>
      items.filter(
        (item) =>
          (!filters.queue || item.queueId === filters.queue) &&
          orgMatches(item, filters.org, unitsById) &&
          rowInRange({ createdAt: item.createdAt, cycleIds: [...(membership.itemToCycles.get(item.id) ?? [])] }, resolved),
      ),
    [items, filters.queue, filters.org, unitsById, membership, resolved],
  );

  // ── Links: the same narrowing as the counts ──────────────────────────────
  const narrowing = useMemo(() => {
    const orgIds = orgAndDescendants(filters.org, orgUnits);
    const clauses = [resolved.pql, filters.queue ? clause('queue', filters.queue) : null, inClause('orgUnit', orgIds)];
    return { clauses, active: clauses.some(Boolean) };
  }, [filters.org, filters.queue, orgUnits, resolved.pql]);

  const stageHref = useCallback(
    (bucket: PipelineBucket) =>
      narrowing.active ? drillToItems(clause('pipelineStage', bucket), ...narrowing.clauses) : `${ITEMS_HREF}?stage=${bucket}`,
    [narrowing],
  );
  const statusHref = useCallback(
    (status: ExitStatus) => {
      if (narrowing.active) return drillToItems(clause('status', status), ...narrowing.clauses);
      return status === 'active' ? ITEMS_HREF : `${ITEMS_HREF}?stage=${status}`;
    },
    [narrowing],
  );
  /** The category mix counts the backlog (active + deferred), so its links do too. */
  const categoryHref = useCallback(
    (key: string, segmentClause: string | null = null) =>
      drillToItems(clause('category', key === UNSET_CATEGORY ? null : key), inClause('status', ['active', 'deferred']), segmentClause, ...narrowing.clauses),
    [narrowing],
  );
  const staleHref = useCallback(
    () => (narrowing.active ? drillToItems(clause('stale', true), clause('status', 'active'), ...narrowing.clauses) : `${ITEMS_HREF}?stale=1`),
    [narrowing],
  );
  const segmentClause = useCallback(
    (group: string) => (segDef ? clause(segDef.pqlField, group || null) : null),
    [segDef],
  );

  // ── Segment names ────────────────────────────────────────────────────────
  const ownerIds = useMemo(() => (segDef?.key === 'owner' ? scoped.map((i) => i.ownerId ?? '').filter(Boolean) : []), [scoped, segDef]);
  const people = useUserNameMap(ownerIds);
  const names: SegmentNames = useMemo(() => ({ orgUnits: unitsById, people }), [unitsById, people]);
  const groupOf = useCallback((item: Item) => (segDef ? segmentValue(item as ItemRow, segDef) ?? '' : ''), [segDef]);
  const groupLabel = useCallback((group: string) => (segDef ? segmentLabel(segDef, group || null, names) : ''), [segDef, names]);

  // ── Funnel ───────────────────────────────────────────────────────────────
  const bucketOf = useMemo(() => {
    const cache = new Map<string, PipelineBucket>();
    return (item: Item): PipelineBucket => {
      let b = cache.get(item.id);
      if (!b) {
        b = classifyPipelineBucket(item, pipelineInputs);
        cache.set(item.id, b);
      }
      return b;
    };
  }, [pipelineInputs]);

  const counts = useMemo(() => {
    const c = Object.fromEntries(Object.keys(PIPELINE_BUCKET_LABEL).map((k) => [k, 0])) as Record<PipelineBucket, number>;
    for (const item of scoped) c[bucketOf(item)] += 1;
    return c;
  }, [scoped, bucketOf]);

  const active = useMemo(() => scoped.filter((i) => itemExitStatus(i) === 'active'), [scoped]);
  /** The backlog: everything not archived. Category mix reads this, not the archive. */
  const backlog = useMemo(() => scoped.filter((i) => itemExitStatus(i) !== 'archived'), [scoped]);

  // ── Status ───────────────────────────────────────────────────────────────
  const statusTotals = useMemo(
    () => Object.fromEntries(EXIT_STATUS_ORDER.map((s) => [s, scoped.filter((i) => itemExitStatus(i) === s).length])) as Record<ExitStatus, number>,
    [scoped],
  );
  const statusRows: StatusRow[] = useMemo(() => {
    if (!segDef) return [{ group: '', label: 'All items', ...statusTotals, total: scoped.length }];
    return breakdown(scoped, groupOf, (i) => itemExitStatus(i))
      .slice(0, 12)
      .map((r) => ({
        group: r.group,
        label: groupLabel(r.group),
        active: r.counts.active ?? 0,
        deferred: r.counts.deferred ?? 0,
        archived: r.counts.archived ?? 0,
        total: r.total,
      }));
  }, [scoped, segDef, statusTotals, groupOf, groupLabel]);

  // ── Category mix ─────────────────────────────────────────────────────────
  const mix = useMemo(() => categoryMix(backlog), [backlog]);
  const categoryColor = useCallback(
    (key: string) => (key === UNSET_CATEGORY ? palette.dim : resolveCategoryHex(key) || CATEGORY_HEX_FALLBACK),
    [palette.dim],
  );
  /** Categories broken down by the segment — not when the segment IS category. */
  const categoryRows: CategoryRow[] = useMemo(() => {
    if (!segDef || segDef.key === 'category') return [];
    return breakdown(backlog, groupOf, (i) => categoryOf(i))
      .slice(0, 12)
      .map((r) => ({ group: r.group, label: groupLabel(r.group), total: r.total, ...r.counts }));
  }, [backlog, segDef, groupOf, groupLabel]);

  // ── Readiness gaps ───────────────────────────────────────────────────────
  const missingFor = useCallback(
    (item: Item) => computeItemReadiness(item, requiredAttributes).required.map((issue) => issue.displayText),
    [requiredAttributes],
  );
  const heat = useMemo(() => readinessGapCells(active, missingFor, HEATMAP_LIMIT), [active, missingFor]);
  const heatAll = useMemo(() => readinessGapCells(active, missingFor, Number.MAX_SAFE_INTEGER).cells, [active, missingFor]);
  const heatSwatch = useCallback(
    (gaps: number): React.CSSProperties => {
      if (gaps === 0) return { background: 'transparent' };
      const t = heat.maxGaps <= 1 ? 1 : gaps / heat.maxGaps;
      return { background: palette.slate, opacity: 0.25 + 0.75 * t };
    },
    [heat.maxGaps, palette.slate],
  );

  // ── Age ──────────────────────────────────────────────────────────────────
  const age = useMemo(() => ageDistribution(active, now, filters.stale), [active, now, filters.stale]);

  // ── What a snapshot and a digest carry ───────────────────────────────────
  const summary = useCallback(
    (): ReportSnapshotSummary => ({
      stats: [
        { label: 'Active items', value: active.length, sub: `${scoped.length} in all` },
        { label: 'Awaiting triage', value: counts.awaiting_triage },
        { label: 'Needs definition', value: counts.needs_definition },
        { label: 'Ready to prioritize', value: counts.ready },
        { label: 'Committed', value: counts.committed },
        { label: `Past ${filters.stale} days`, value: age.pastThreshold, sub: 'since last change' },
      ],
      series: [
        { title: 'Funnel', description: 'Where active items stand', rows: PIPELINE_STAGES.map((s) => ({ label: s.label, value: counts[s.key], href: stageHref(s.key), color: palette.slate })) },
        { title: 'Status', rows: EXIT_STATUS_ORDER.map((s) => ({ label: EXIT_STATUS_LABEL[s], value: statusTotals[s], href: statusHref(s), color: exitStatusColor[s] })) },
        { title: 'Category mix', description: 'Active and deferred', rows: mix.map((m) => ({ label: categoryLabel(m.key), value: m.count, href: categoryHref(m.key), color: categoryColor(m.key) })) },
        ...(segDef
          ? [{ title: `Items by ${segDef.label.toLowerCase()}`, rows: statusRows.map((r) => ({ label: r.label, value: r.total, href: drillToItems(segmentClause(r.group), ...narrowing.clauses), color: palette.slate })) }]
          : []),
      ],
    }),
    [active.length, scoped.length, counts, filters.stale, age.pastThreshold, stageHref, statusTotals, statusHref, exitStatusColor, mix, categoryHref, categoryColor, segDef, statusRows, segmentClause, narrowing, palette.slate],
  );

  const csvRows = useCallback(
    () =>
      scoped.map((item) => ({
        id: item.id,
        title: item.title,
        stage: PIPELINE_BUCKET_LABEL[bucketOf(item)],
        status: itemExitStatus(item),
        category: item.itemCategory ?? '',
        queue: item.queueId ? unitName(item.queueId, unitsById, '') : '',
        org_unit: item.orgUnitId ? unitName(item.orgUnitId, unitsById, '') : '',
        ...(segDef ? { [segDef.label.toLowerCase()]: groupLabel(groupOf(item)) } : {}),
        days_since_update: daysSince(item.updatedAt ?? item.createdAt, now) ?? '',
        missing_required_inputs: missingFor(item).join('; '),
        created: item.createdAt ? new Date(item.createdAt).toISOString().slice(0, 10) : '',
      })),
    [scoped, bucketOf, unitsById, segDef, groupLabel, groupOf, now, missingFor],
  );

  return {
    items, storeItems, spaceWords, itemsLoading, loadError, scoped, active, backlog, counts, statusRows, statusTotals, mix, categoryRows,
    heat, heatAll, heatSwatch, age, palette, exitStatusColor, categoryColor, queues, orgOptions, orgUnits,
    segDef, stageHref, statusHref, categoryHref, staleHref, segmentClause, narrowing, summary, csvRows,
  };
}

type PipelineModel = ReturnType<typeof usePipelineHealthModel>;

// ============================================================================
// Small presentational pieces
// ============================================================================

function StatTile({ label, value, hint, href }: { label: string; value: number; hint?: string; href?: string }) {
  const body = (
    <>
      <div className={MONO_LABEL}>{label}</div>
      <div className="mt-1 text-3xl leading-none tabular-nums text-[var(--text-pri)]" style={{ fontFamily: 'var(--serif)' }}>{value}</div>
      {hint && <div className="mt-1 text-xs text-[var(--text-ter)]">{hint}</div>}
    </>
  );
  const cls = 'block border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3';
  return href ? (
    <Link to={href} className={`${cls} transition-colors hover:bg-[var(--hover-tint)]`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  );
}

/**
 * The funnel: one row per stage, bars centred so the shape narrows as items
 * advance. Each row is a link — the stage count IS the row count of the list
 * it opens, because both come from classifyPipelineBucket and carry the same
 * narrowing.
 */
function Funnel({ counts, activeTotal, hrefFor }: { counts: Record<PipelineBucket, number>; activeTotal: number; hrefFor: (b: PipelineBucket) => string }) {
  const max = Math.max(1, ...PIPELINE_STAGES.map((s) => counts[s.key]));
  return (
    <div className="space-y-1">
      {PIPELINE_STAGES.map((stage, i) => {
        const n = counts[stage.key];
        const width = n === 0 ? 0 : Math.max(3, (n / max) * 100);
        return (
          <Link
            key={stage.key}
            to={hrefFor(stage.key)}
            title={stage.description}
            className="group grid grid-cols-[11rem_1fr_6rem] items-center gap-3 py-1.5 transition-colors hover:bg-[var(--hover-tint)]"
            aria-label={`${stage.label}: ${n} items. Open the list.`}
          >
            <span className="flex items-baseline gap-2">
              <span className="font-mono text-[11px] text-[var(--text-dim)]">0{i + 1}</span>
              <span className="text-sm text-[var(--text-pri)]">{stage.label}</span>
            </span>
            <span className="flex h-6 items-center justify-center">
              <span
                className="h-full"
                style={{
                  width: `${width}%`,
                  background: 'var(--slate)',
                  opacity: n === 0 ? 0 : 0.35 + 0.65 * (i / Math.max(1, PIPELINE_STAGES.length - 1)),
                }}
              />
            </span>
            <span className="text-right font-mono text-xs tabular-nums text-[var(--text-sec)]">
              <span className="text-[var(--text-pri)]">{n}</span>
              <span className="ml-1 text-[var(--text-dim)]">{percent(n, activeTotal)}</span>
            </span>
          </Link>
        );
      })}
    </div>
  );
}

function ExitRow({ counts, hrefFor }: { counts: Record<PipelineBucket, number>; hrefFor: (b: PipelineBucket) => string }) {
  return (
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-t border-[var(--rule)] pt-3">
      <span className={MONO_LABEL}>Not in the funnel</span>
      {PIPELINE_EXITS.map((exit) => (
        <Link key={exit.key} to={hrefFor(exit.key)} title={exit.description} className="text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)]">
          {exit.label} <span className="font-mono text-xs tabular-nums text-[var(--text-pri)]">{counts[exit.key]}</span>
        </Link>
      ))}
    </div>
  );
}

/** Swatches explaining the heatmap's fill scale. */
function HeatLegend({ maxGaps, swatch }: { maxGaps: number; swatch: (gaps: number) => React.CSSProperties }) {
  const steps = Array.from({ length: Math.max(1, maxGaps) + 1 }, (_, i) => i);
  return (
    <div className="flex items-center gap-2">
      <span className={MONO_LABEL}>Missing inputs</span>
      {steps.map((g) => (
        <span key={g} className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 border border-[var(--rule)]" style={swatch(g)} />
          <span className="font-mono text-[11px] tabular-nums text-[var(--text-ter)]">{g}</span>
        </span>
      ))}
    </div>
  );
}

const heatColumns: Column<ReadinessGapCell>[] = [
  {
    key: 'title',
    header: 'Item',
    render: (row) => (
      <Link to={`${ITEMS_HREF}/${row.id}`} className="text-[var(--text-pri)] hover:underline">
        {row.title}
      </Link>
    ),
  },
  { key: 'gaps', header: 'Missing', numeric: true, width: 96, sortable: true },
  {
    key: 'missing',
    header: 'What is missing',
    render: (row) => <span className="text-[var(--text-sec)]">{row.missing.length ? row.missing.join(' · ') : 'Nothing — ready'}</span>,
  },
];

// ============================================================================
// The body: shared by the page and its print view
// ============================================================================

function PipelineHealthBody({ m, filters, print = false }: { m: PipelineModel; filters: Filters; print?: boolean }) {
  const navigate = useNavigate();
  const [heatView, setHeatView] = useState<'grid' | 'table'>('grid');
  const go = (href: string) => {
    if (!print) navigate(href);
  };

  // Keep the heatmap tab sensible when the population changes under it.
  useEffect(() => {
    if (m.heat.total === 0 && heatView === 'table') setHeatView('grid');
  }, [m.heat.total, heatView]);

  const ageColor = (severity: 'none' | 'warning' | 'serious') =>
    severity === 'serious' ? m.palette.clay : severity === 'warning' ? m.palette.gold : m.palette.slate;
  const segLabel = m.segDef?.label.toLowerCase();
  const loading = m.itemsLoading && m.storeItems.length === 0;

  if (loading) {
    return (
      <div className="space-y-3">
        <Skeleton height={72} />
        <Skeleton height={280} />
        <Skeleton height={220} />
      </div>
    );
  }

  if (m.storeItems.length === 0 && m.loadError) {
    return (
      <Panel>
        <EmptyState
          icon={ListChecks}
          title="Items could not be loaded"
          description={`Nothing on this page is counted, because the read failed: ${m.loadError}. Reload to try again.`}
        />
      </Panel>
    );
  }

  if (m.scoped.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={ListChecks}
          title={m.storeItems.length === 0 ? 'No items yet' : m.items.length === 0 ? 'No items in this space' : 'No items match these filters'}
          description={
            m.storeItems.length === 0
              ? 'The pipeline fills as ideas are submitted and items are created.'
              : m.items.length === 0
                ? 'This report counts the items the item list shows in your current space. Switch space in the side bar, or use the list’s “Show all spaces”.'
                : 'Widen the range, queue or org unit.'
          }
          action={
            m.items.length === 0 ? (
              <Link to={ITEMS_HREF} className="text-sm text-[var(--text-pri)] underline">
                Go to items
              </Link>
            ) : undefined
          }
        />
      </Panel>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Headline numbers ─────────────────────────────────────────── */}
      <div className={`grid gap-3 ${print ? 'grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'}`}>
        <StatTile label="Active items" value={m.active.length} hint={`${m.scoped.length} in all`} href={m.statusHref('active')} />
        <StatTile label="Awaiting triage" value={m.counts.awaiting_triage} href={m.stageHref('awaiting_triage')} />
        <StatTile label="Needs definition" value={m.counts.needs_definition} href={m.stageHref('needs_definition')} />
        <StatTile label="Ready to prioritize" value={m.counts.ready} href={m.stageHref('ready')} />
        <StatTile label="Committed" value={m.counts.committed} href={m.stageHref('committed')} />
        <StatTile
          label={`Past ${filters.stale} days`}
          value={m.age.pastThreshold}
          hint={m.age.serious ? `${m.age.serious} past ${filters.stale * 2}` : 'since last change'}
        />
      </div>

      {/* ── 01 Funnel ───────────────────────────────────────────────── */}
      <Panel label="01 · Funnel" title="Where active items stand">
        <p className="mb-3 text-xs text-[var(--text-ter)]">
          Each active item is placed at the furthest point it has reached. Click a stage to open the item list filtered to it
          {m.narrowing.active ? ', with this page’s range and filters' : ''}.
        </p>
        <Funnel counts={m.counts} activeTotal={m.active.length} hrefFor={m.stageHref} />
        <ExitRow counts={m.counts} hrefFor={m.stageHref} />
      </Panel>

      <div className={`grid gap-6 ${print ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
        {/* ── 02 Status ───────────────────────────────────────────────── */}
        <div data-report-chart="" data-chart-title="Status">
          <Panel label="02 · Status" title={m.segDef ? `Status by ${segLabel}` : 'Active, deferred, archived'}>
            <div style={{ height: m.segDef ? Math.max(140, 32 * m.statusRows.length + 48) : 96 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={m.statusRows} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }} barCategoryGap={8}>
                  <XAxis type="number" hide allowDecimals={false} />
                  <YAxis type="category" dataKey="label" width={m.segDef ? 120 : 0} hide={!m.segDef} {...m.palette.theme.axis} />
                  <ChartTooltip {...m.palette.theme.tooltip} cursor={{ fill: 'transparent' }} />
                  {EXIT_STATUS_ORDER.map((s) => (
                    <Bar
                      key={s}
                      dataKey={s}
                      name={EXIT_STATUS_LABEL[s]}
                      stackId="status"
                      fill={m.exitStatusColor[s]}
                      stroke={m.palette.theme.tooltip.contentStyle.background as string}
                      strokeWidth={2}
                      isAnimationActive={false}
                      cursor={print ? undefined : 'pointer'}
                      onClick={(d) => {
                        const row = clickedRow<StatusRow>(d);
                        if (!row) return;
                        go(m.segDef ? drillToItems(m.segmentClause(row.group), clause('status', s), ...m.narrowing.clauses) : m.statusHref(s));
                      }}
                    >
                      {!m.segDef && <LabelList dataKey={s} position="center" fill="#fff" fontSize={11} fontFamily="JetBrains Mono, monospace" formatter={(v) => (Number(v) > 0 ? v : '')} />}
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
              {EXIT_STATUS_ORDER.map((s) => (
                <Link key={s} to={m.statusHref(s)} className="flex items-center gap-2 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)]">
                  <span className="inline-block h-2.5 w-2.5" style={{ background: m.exitStatusColor[s] }} />
                  {EXIT_STATUS_LABEL[s]}
                  <span className="font-mono tabular-nums text-[var(--text-pri)]">{m.statusTotals[s]}</span>
                </Link>
              ))}
            </div>
          </Panel>
        </div>

        {/* ── 03 Category mix ─────────────────────────────────────────── */}
        <div data-report-chart="" data-chart-title="Category mix">
          <Panel label="03 · Category mix" title={m.categoryRows.length ? `Categories by ${segLabel}` : 'What kind of items the backlog holds'}>
            <p className="mb-2 text-xs text-[var(--text-ter)]">Active and deferred items; the archive is left out.</p>
            {m.categoryRows.length === 0 ? (
              <div className="grid grid-cols-[9rem_1fr] items-center gap-4">
                <div className="h-36">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={m.mix}
                        dataKey="count"
                        nameKey="key"
                        cx="50%"
                        cy="50%"
                        innerRadius={38}
                        outerRadius={64}
                        paddingAngle={2}
                        stroke={m.palette.theme.tooltip.contentStyle.background as string}
                        strokeWidth={2}
                        isAnimationActive={false}
                        cursor={print ? undefined : 'pointer'}
                        onClick={(d) => {
                          const slice = clickedRow<{ key: string }>(d);
                          if (slice?.key) go(m.categoryHref(slice.key));
                        }}
                      >
                        {m.mix.map((slice) => (
                          <Cell key={slice.key} fill={m.categoryColor(slice.key)} />
                        ))}
                      </Pie>
                      <ChartTooltip {...m.palette.theme.tooltip} formatter={(value, name) => [value ?? 0, categoryLabel(String(name ?? ''))]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="space-y-1.5">
                  {m.mix.map((slice) => (
                    <li key={slice.key}>
                      <Link to={m.categoryHref(slice.key)} className="flex items-center gap-2 text-xs text-[var(--text-sec)] hover:text-[var(--text-pri)]">
                        <span className="inline-block h-2.5 w-2.5 shrink-0" style={{ background: m.categoryColor(slice.key) }} />
                        <span className="flex-1 truncate">{categoryLabel(slice.key)}</span>
                        <span className="font-mono tabular-nums text-[var(--text-pri)]">{slice.count}</span>
                        <span className="w-9 text-right font-mono tabular-nums text-[var(--text-dim)]">{percent(slice.count, m.backlog.length)}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div style={{ height: Math.max(140, 32 * m.categoryRows.length + 48) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={m.categoryRows} layout="vertical" margin={{ top: 4, right: 24, bottom: 4, left: 4 }} barCategoryGap={8}>
                    <XAxis type="number" hide allowDecimals={false} />
                    <YAxis type="category" dataKey="label" width={120} {...m.palette.theme.axis} />
                    <ChartTooltip {...m.palette.theme.tooltip} cursor={{ fill: 'transparent' }} formatter={(value, name) => [value ?? 0, categoryLabel(String(name ?? ''))]} />
                    <Legend {...m.palette.theme.legend} formatter={(value) => categoryLabel(String(value))} />
                    {m.mix.map((slice) => (
                      <Bar
                        key={slice.key}
                        dataKey={slice.key}
                        name={slice.key}
                        stackId="cat"
                        fill={m.categoryColor(slice.key)}
                        stroke={m.palette.theme.tooltip.contentStyle.background as string}
                        strokeWidth={2}
                        isAnimationActive={false}
                        cursor={print ? undefined : 'pointer'}
                        onClick={(d) => {
                          const row = clickedRow<CategoryRow>(d);
                          if (row) go(m.categoryHref(slice.key, m.segmentClause(String(row.group))));
                        }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* ── 04 Readiness gaps ───────────────────────────────────────── */}
      <Panel
        label="04 · Readiness gaps"
        title="Which active items are missing required inputs"
        actions={
          print ? undefined : (
            <Tabs
              ariaLabel="Readiness view"
              active={heatView}
              onChange={setHeatView}
              groups={[{ tabs: [{ id: 'grid', label: 'Grid', icon: Grid3x3 }, { id: 'table', label: 'Table', icon: TableProperties }] }]}
            />
          )
        }
        flush={!print && heatView === 'table'}
      >
        {print || heatView === 'grid' ? (
          <>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <HeatLegend maxGaps={m.heat.maxGaps} swatch={m.heatSwatch} />
              <span className="text-xs text-[var(--text-ter)]">
                {m.heat.total > m.heat.cells.length
                  ? `Showing the ${m.heat.cells.length} items with the most gaps of ${m.heat.total}. The table lists all of them.`
                  : `${m.heat.total} active items. Darker means more required inputs missing; click a cell to open the item.`}
              </span>
            </div>
            {m.heat.total === 0 ? (
              <EmptyState size="inline" title="No active items" description="There is nothing to make ready." />
            ) : (
              <div className="flex flex-wrap gap-[3px]" role="list" aria-label="Items by missing required inputs">
                {m.heat.cells.map((cell) => (
                  <Link
                    key={cell.id}
                    to={`${ITEMS_HREF}/${cell.id}`}
                    role="listitem"
                    title={`${cell.title} — ${cell.gaps === 0 ? 'ready' : `${cell.gaps} missing: ${cell.missing.join(', ')}`}`}
                    aria-label={`${cell.title}, ${cell.gaps} missing inputs`}
                    className="h-4 w-4 border border-[var(--rule-bright)] transition-transform hover:scale-125"
                    style={m.heatSwatch(cell.gaps)}
                  />
                ))}
              </div>
            )}
          </>
        ) : (
          <DataTable<ReadinessGapCell>
            rows={m.heatAll}
            getRowKey={(row) => row.id}
            columns={heatColumns}
            density="compact"
            maxHeight={420}
            stickyHeader
            caption="Active items and the required inputs each is missing"
            captionHidden
            empty={<EmptyState size="inline" title="No active items" />}
          />
        )}
      </Panel>

      {/* ── 05 Age ──────────────────────────────────────────────────── */}
      <div data-report-chart="" data-chart-title="Age">
        <Panel label="05 · Age" title="How long active items have sat">
          <p className="mb-3 text-xs text-[var(--text-ter)]">
            Days since each active item last changed — the same measure as the list's Stale filter. Past {filters.stale} days is marked in gold; past {filters.stale * 2} in clay.
            {m.age.unknown > 0 && ` ${m.age.unknown} items carry no usable date and are not shown.`}
          </p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={m.age.buckets} margin={{ top: 16, right: 8, bottom: 4, left: 0 }} barCategoryGap="18%">
                <XAxis dataKey="label" {...m.palette.theme.axis} interval={0} />
                <YAxis allowDecimals={false} width={32} {...m.palette.theme.axis} />
                <ChartTooltip {...m.palette.theme.tooltip} cursor={{ fill: 'transparent' }} formatter={(value) => [value ?? 0, 'Items']} />
                <Bar dataKey="count" isAnimationActive={false}>
                  <LabelList dataKey="count" position="top" fill={m.palette.textSec} fontSize={11} fontFamily="JetBrains Mono, monospace" />
                  {m.age.buckets.map((b) => (
                    <Cell key={b.key} fill={ageColor(b.severity)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {m.age.pastThreshold > 0 && filters.stale === 30 && (
            <div className="mt-3">
              <Callout tone="mustard" label="Stale">
                {m.age.pastThreshold} active items have not changed in more than 30 days.{' '}
                <Link to={m.staleHref()} className="underline">
                  Open them in the list
                </Link>
                .
              </Callout>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}

// ============================================================================
// Page and print view
// ============================================================================

function PipelineFilters({ m, filters, setFilter, controls }: { m: PipelineModel; filters: Filters; setFilter: (k: keyof Filters, v: string) => void; controls: ReportControls }) {
  const filtersActive = Boolean(filters.queue || filters.org || controls.range.key !== 'all');
  return (
    <>
      <Field label={<span className={MONO_LABEL}>Queue</span>} htmlFor="ph-queue">
        <Select
          id="ph-queue"
          size="sm"
          value={filters.queue}
          onChange={(e) => setFilter('queue', e.target.value)}
          options={[{ value: '', label: m.queues.length ? 'Every queue' : 'No queues configured' }, ...m.queues.map((q) => ({ value: q.id, label: q.displayName ?? q.name }))]}
          disabled={m.queues.length === 0}
        />
      </Field>
      <Field label={<span className={MONO_LABEL}>Org unit</span>} htmlFor="ph-org">
        <Select
          id="ph-org"
          size="sm"
          value={filters.org}
          onChange={(e) => setFilter('org', e.target.value)}
          options={[{ value: '', label: m.orgUnits.length ? 'Every org unit' : 'No org units' }, ...m.orgOptions]}
          disabled={m.orgUnits.length === 0}
        />
      </Field>
      <Field label={<span className={MONO_LABEL}>Stale after</span>} htmlFor="ph-stale">
        <Select
          id="ph-stale"
          size="sm"
          value={String(filters.stale)}
          onChange={(e) => setFilter('stale', e.target.value)}
          options={STALE_THRESHOLDS.map((d) => ({ value: String(d), label: `${d} days` }))}
        />
      </Field>
      {filtersActive && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setFilter('queue', '');
            setFilter('org', '');
            controls.setRange({ key: 'all' });
          }}
        >
          Clear filters
        </Button>
      )}
      {m.spaceWords && (
        <span className="self-center font-mono text-[11px] uppercase tracking-[0.08em] text-[var(--text-ter)]" data-testid="pipeline-health-space">
          {m.spaceWords.label}
        </span>
      )}
    </>
  );
}

export function PipelineHealthReport() {
  const controls = useReportControls();
  const { filters, setFilter } = usePipelineFilters();
  const m = usePipelineHealthModel(controls, filters);
  return (
    <ReportFrame
      reportKey={REPORT_KEY}
      reportPath={REPORT_PATH}
      name={REPORT_NAME}
      question={QUESTION}
      description="Every number is counted the way the Triage Queue, the Needs Definition page and the item list count it, and every one opens the items behind it."
      controls={controls}
      rangeNote="Windows count items by created date."
      csv={{ filename: 'pipeline-health', rows: m.csvRows }}
      summary={m.summary}
      unavailableReason={m.storeItems.length === 0 && m.loadError ? 'Items could not be loaded, so there are no numbers to save or send.' : null}
      controlsExtra={<PipelineFilters m={m} filters={filters} setFilter={setFilter} controls={controls} />}
    >
      <PipelineHealthBody m={m} filters={filters} />
    </ReportFrame>
  );
}

export function PipelineHealthPrintPage() {
  const controls = useReportControls();
  const { filters } = usePipelineFilters();
  const m = usePipelineHealthModel(controls, filters);
  const parts = [controls.resolved.label];
  if (m.segDef) parts.push(`by ${m.segDef.label}`);
  if (filters.queue) parts.push(`queue: ${m.queues.find((q) => q.id === filters.queue)?.displayName ?? filters.queue}`);
  if (filters.org) parts.push(`org unit: ${m.orgOptions.find((o) => o.value === filters.org)?.label.trim() ?? filters.org}`);
  if (m.spaceWords) parts.push(m.spaceWords.label);
  parts.push(new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }));
  return (
    <ReportPrintShell title={REPORT_NAME} backHref={`${REPORT_PATH}${window.location.search}`} meta={`${QUESTION} · ${parts.join(' · ')}`}>
      <PipelineHealthBody m={m} filters={filters} print />
    </ReportPrintShell>
  );
}

export default PipelineHealthReport;
